/**
 * THE LIBRARY'S DATA, FOR ONE VIEW AND ONE PAGE OF IT.
 *
 * `useLibraryData` turns a description of a view (libraryQuery.ts) and a
 * position in it into rows, a total and whether there is more, from
 * whichever engine answers: the database when nothing is typed, the search
 * index when something is, a handful of reads for a named set. The page and
 * the list beside an open policy both use it, so they cannot show different
 * lists for the same description.
 *
 * `useLibraryRoute` is the same thing wired to the address bar and the
 * browser's history: the description lives in the address, and the position
 * (which page, and where each page before it ended) lives in the history
 * entry, so Back, Forward and Refresh come back to the same page of the same
 * list without a cursor ever appearing in a link.
 *
 * AT ANY MOMENT THE BROWSER HOLDS ONE PAGE. Nothing here accumulates pages,
 * and nothing reads a library.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../../auth/useAuth';
import { useVisible } from '../../org/useVisibility';
import { hasAtLeast } from '../../../types/roles';
import { call } from '../../../lib/callable';
import {
  isNamedSet, libraryParams, parseLibraryState, searchShapeFor, seesOwners, shapeFor, usesSearch,
  type LibraryState, type LibraryView, type Who,
} from './libraryQuery';
import { countLibrary, fetchFormerMembers, fetchLibraryPage, fetchPoliciesByIds, type Cursor, type LibraryRow } from './libraryPage';
import { useLibrarySearch } from './useLibrarySearch';
import { ATTENTION_VIEWS } from './libraryText';

/** Who is looking, as the library needs to know it. */
export interface LibraryWho extends Who {
  orgId: string | null;
  mayUpload: boolean;
  isAdmin: boolean;
  ownersShown: boolean;
}

export function useLibraryWho(): LibraryWho {
  const { session } = useAuth();
  const visible = useVisible();
  const roles = useMemo(() => session?.roles ?? [], [session?.roles]);
  return useMemo(() => {
    const who: Who = { uid: session?.uid ?? '', roles, mayReadRetired: visible.retiredPolicies };
    return {
      ...who,
      orgId: session?.orgId ?? null,
      mayUpload: hasAtLeast(roles, 'author'),
      isAdmin: hasAtLeast(roles, 'admin'),
      ownersShown: seesOwners(who),
    };
  }, [session?.uid, session?.orgId, roles, visible.retiredPolicies]);
}

/** Which page, and where each page before it ended. `cursors[i]` starts page i + 1. */
export interface Paging { page: number; cursors: Cursor[] }
export const FIRST_PAGE: Paging = { page: 0, cursors: [] };

export interface LibraryData {
  /** Which engine answered, for wording only: the reader is never asked to care. */
  mode: 'list' | 'search' | 'named' | 'unsupported';
  rows: LibraryRow[];
  /** How many policies the view holds in all. Null while it is being counted, or when it could not be. */
  total: number | null;
  /** 1-based positions of the first and last row shown. */
  from: number;
  to: number;
  hasPrevious: boolean;
  hasNext: boolean;
  /** Where this page ends, to hand to the next one. Null in search and on the last page. */
  nextCursor: Cursor | null;
  /** Nothing to show yet. */
  isLoading: boolean;
  /** Showing the last answer while the next arrives. */
  isUpdating: boolean;
  error: unknown;
  /** Why the search index cannot answer this view, when it cannot. */
  unsupported: string | null;
  /** More matched than can be paged to. */
  tooMany: boolean;
  retry: () => void;
}

export function useLibraryData(who: LibraryWho, state: LibraryState, paging: Paging, now: Date): LibraryData {
  const { orgId } = who;
  const named = isNamedSet(state);
  const searching = !named && usesSearch(state);
  const searchShape = useMemo(() => (searching ? searchShapeFor(state, who, now) : null), [searching, state, who, now]);
  const unsupported = searchShape && 'unsupported' in searchShape ? searchShape.unsupported : null;
  const listing = !named && !searching;

  const shape = useMemo(() => shapeFor(state, who, now), [state, who, now]);
  const shapeKey = useMemo(() => JSON.stringify(shape), [shape]);
  // A page asked for without knowing where the one before it ended is the first page.
  const after = paging.page > 0 ? paging.cursors[paging.page - 1] ?? null : null;
  const page = paging.page > 0 && !after && listing ? 0 : paging.page;

  const list = useQuery({
    queryKey: ['library', orgId, state.view, shapeKey, state.size, after, who.ownersShown],
    enabled: Boolean(orgId) && listing,
    /*
     * The last answer stays on screen while the next arrives, so the table
     * does not blink for a filter, a sort or the next page. NOT across views:
     * the rows of "Owner inactive" sitting under the heading "Owner not
     * assigned" for half a second would be the page saying something false.
     * A different view starts from its loading state.
     */
    placeholderData: (previous, previousQuery) => (previousQuery?.queryKey[2] === state.view ? previous : undefined),
    queryFn: () => fetchLibraryPage(orgId!, shape, state.size, after, who.ownersShown),
  });
  const total = useQuery({
    queryKey: ['library-count', orgId, JSON.stringify(shape.where), shape.collection],
    enabled: Boolean(orgId) && listing,
    staleTime: 60_000,
    queryFn: () => countLibrary(orgId!, shape),
  });
  const search = useLibrarySearch(orgId, state.text, searchShape && !('unsupported' in searchShape) ? searchShape : null, page, who.ownersShown);
  const set = useQuery({
    queryKey: ['library-named', orgId, state.ids.join(','), who.ownersShown],
    enabled: Boolean(orgId) && named,
    queryFn: () => fetchPoliciesByIds(orgId!, state.ids, who.ownersShown),
  });

  const offset = page * state.size;
  if (named) {
    const rows = set.data ?? [];
    return {
      mode: 'named', rows, total: set.data ? rows.length : null, from: rows.length ? 1 : 0, to: rows.length,
      hasPrevious: false, hasNext: false, nextCursor: null, isLoading: set.isLoading, isUpdating: false,
      error: set.error, unsupported: null, tooMany: false, retry: () => void set.refetch(),
    };
  }
  if (unsupported) {
    return {
      mode: 'unsupported', rows: [], total: null, from: 0, to: 0, hasPrevious: false, hasNext: false, nextCursor: null,
      isLoading: false, isUpdating: false, error: null, unsupported, tooMany: false, retry: () => undefined,
    };
  }
  if (searching) {
    const rows = search.data?.rows ?? [];
    return {
      mode: 'search', rows, total: search.data ? search.data.total : null,
      from: rows.length ? offset + 1 : 0, to: offset + rows.length,
      hasPrevious: page > 0, hasNext: search.data ? page < search.data.lastPage : false, nextCursor: null,
      isLoading: search.isLoading && !search.data, isUpdating: search.isFetching && Boolean(search.data),
      error: search.error, unsupported: null, tooMany: search.data?.tooMany ?? false, retry: () => void search.refetch(),
    };
  }
  const rows = list.data?.rows ?? [];
  return {
    mode: 'list', rows,
    // A count that failed is not a reason to fail the list: the range is shown without "of N".
    total: total.data ?? null,
    from: rows.length ? offset + 1 : 0, to: offset + rows.length,
    hasPrevious: page > 0, hasNext: Boolean(list.data?.next), nextCursor: list.data?.next ?? null,
    isLoading: list.isLoading && !list.data, isUpdating: list.isFetching && Boolean(list.data),
    error: list.error, unsupported: null, tooMany: false, retry: () => { void list.refetch(); void total.refetch(); },
  };
}

// ---------------------------------------------------------------- the address and the history

/** What a link from the list carries in its history entry, so the page it came from can be returned to and shown beside it. */
export interface LibraryLinkState { lib: { key: string; page: number; cursors: Cursor[] } }

function pagingFrom(locationState: unknown, key: string): Paging {
  const lib = (locationState as Partial<LibraryLinkState> | null)?.lib;
  if (!lib || lib.key !== key || !Number.isInteger(lib.page) || lib.page < 0 || !Array.isArray(lib.cursors)) return FIRST_PAGE;
  return { page: lib.page, cursors: lib.cursors };
}

export interface LibraryRoute {
  who: LibraryWho;
  state: LibraryState;
  paging: Paging;
  data: LibraryData;
  now: Date;
  /** The description as it reads in the address: what a link to this view carries. */
  search: string;
  /** For links out of the list: the position, so it can be come back to. */
  linkState: LibraryLinkState;
  /** Change the view. Always back to its first page. `replace` for typing, so Back is not one keystroke. */
  change: (patch: Partial<LibraryState>, opts?: { replace?: boolean }) => void;
  next: () => void;
  previous: () => void;
}

export function useLibraryRoute(): LibraryRoute {
  const who = useLibraryWho();
  const [params] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  // One "today" for the life of the page, so a view does not change under a reader at midnight mid-click.
  const [now] = useState(() => new Date());

  const state = useMemo(() => parseLibraryState(params, who), [params, who]);
  const search = useMemo(() => libraryParams(state, who).toString(), [state, who]);
  const paging = useMemo(() => pagingFrom(location.state, search), [location.state, search]);
  const data = useLibraryData(who, state, paging, now);

  /*
   * A change is applied to what was last ASKED FOR, not to what is on screen.
   * The address updates at once and the screen a moment later, so two changes
   * in quick succession ("Clear filters", then a sort) would otherwise both
   * start from the same picture, and the second would put back what the first
   * took away. Cleared as soon as the screen has caught up.
   */
  const asked = useRef<LibraryState | null>(null);
  useEffect(() => { asked.current = null; }, [search]);

  const change = useCallback((patch: Partial<LibraryState>, opts?: { replace?: boolean }) => {
    const nextState = { ...(asked.current ?? state), ...patch };
    asked.current = nextState;
    const nextSearch = libraryParams(nextState, who).toString();
    navigate({ pathname: location.pathname, search: nextSearch ? `?${nextSearch}` : '' }, { replace: opts?.replace ?? false });
  }, [state, who, navigate, location.pathname]);

  const go = useCallback((to: Paging) => {
    const lib: LibraryLinkState = { lib: { key: search, page: to.page, cursors: to.cursors } };
    navigate({ pathname: location.pathname, search: search ? `?${search}` : '' }, { state: lib });
  }, [navigate, location.pathname, search]);

  const next = useCallback(() => {
    if (!data.hasNext) return;
    const cursors = data.nextCursor ? [...paging.cursors.slice(0, paging.page), data.nextCursor] : paging.cursors;
    go({ page: paging.page + 1, cursors });
  }, [data.hasNext, data.nextCursor, paging, go]);

  const previous = useCallback(() => {
    if (paging.page > 0) go({ page: paging.page - 1, cursors: paging.cursors });
  }, [paging, go]);

  const linkState = useMemo<LibraryLinkState>(() => ({ lib: { key: search, page: paging.page, cursors: paging.cursors } }), [search, paging]);

  return { who, state, paging, data, now, search, linkState, change, next, previous };
}

// ---------------------------------------------------------------- counts, and the names behind two filters

export type AttentionCounts = Partial<Record<LibraryView, number | null>>;

/**
 * How many policies stand in each condition that needs somebody's attention.
 * Each is a count of the very query its line opens, so a number and the list
 * behind it cannot disagree by construction.
 */
export function useAttentionCounts(who: LibraryWho, now: Date, enabled: boolean) {
  return useQuery({
    queryKey: ['library-attention', who.orgId, who.uid, who.mayReadRetired],
    enabled: enabled && Boolean(who.orgId),
    staleTime: 60_000,
    queryFn: async (): Promise<AttentionCounts> => {
      const base: LibraryState = { ...parseLibraryState(new URLSearchParams(), who) };
      const counted = await Promise.all(ATTENTION_VIEWS.map(async (view) => {
        try { return [view, await countLibrary(who.orgId!, shapeFor({ ...base, view }, who, now))] as const; }
        // One count refused or failed leaves its line without a number, not the page without a list.
        catch { return [view, null] as const; }
      }));
      return Object.fromEntries(counted);
    },
  });
}

/** The people a policy can be owned by, for the Owner filter. Authors and above; the server refuses anybody else. */
export function useOwnerDirectory(enabled: boolean) {
  return useQuery({
    queryKey: ['library-owners'],
    enabled,
    staleTime: 5 * 60_000,
    queryFn: async () => (await call<Record<string, never>, { owners: { uid: string; name: string }[] }>('getPolicyOwnerDirectory', {})).owners,
  });
}

/**
 * The people who have left, for narrowing the Owner inactive queue to one of
 * them. Administrators only, as the queue is: the member list is theirs to
 * read (firestore.rules), and nobody else is asked for it.
 */
export function useFormerOwners(who: LibraryWho, enabled: boolean) {
  return useQuery({
    queryKey: ['library-former-owners', who.orgId],
    enabled: enabled && who.isAdmin && Boolean(who.orgId),
    staleTime: 5 * 60_000,
    queryFn: () => fetchFormerMembers(who.orgId!),
  });
}
